import { useParams, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { ArrowLeft, MessageSquare, Send, ThumbsUp, User } from 'lucide-react'

export default function CourseDetailPage() {
  const { courseId } = useParams()
  const navigate = useNavigate()
  const [replyText, setReplyText] = useState('')
  const [studentReplyText, setStudentReplyText] = useState('')

  // Mock question detail
  const question = {
    id: courseId,
    student: 'Sarah Johnson',
    studentAvatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=0284c7&color=fff',
    question: 'How do I complete the first exercise in Module 2?',
    description: 'I\'ve been trying to complete the exercise for 2 hours now. I understand the concept but I\'m not sure how to apply it to the problem. Can someone help me with the approach?',
    course: 'Python Fundamentals',
    category: 'exercise',
    timestamp: '2 hours ago',
    helpful: 5,
    views: 24,
    answers: [
      {
        id: 1,
        author: 'Instructor - Mike Chen',
        avatar: 'https://ui-avatars.com/api/?name=Mike+Chen&background=10b981&color=fff',
        isInstructor: true,
        text: 'Great question! Here\'s the approach: First, break down the problem into smaller steps. You\'ll need to loop through the array and check each element. Let me know if you need more specific guidance!',
        timestamp: '1 hour ago',
        helpful: 8,
      },
      {
        id: 2,
        author: 'John Davis',
        avatar: 'https://ui-avatars.com/api/?name=John+Davis&background=f59e0b&color=fff',
        isInstructor: false,
        text: 'I had the same issue! The key is to remember what you learned in lesson 3 about list comprehensions. That might help you solve this more elegantly.',
        timestamp: '30 minutes ago',
        helpful: 3,
      },
    ],
  }

  const handlePostReply = () => {
    if (replyText.trim()) {
      console.log('Posted:', replyText)
      setReplyText('')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <button
        onClick={() => navigate('/training')}
        className="flex items-center space-x-2 text-cyber-600 hover:text-cyber-700 font-medium"
      >
        <ArrowLeft size={20} />
        <span>Back to Student Questions</span>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Question */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-white">{question.question}</h1>
                  <div className="flex items-center space-x-4 mt-3 text-sm text-blue-200">
                    <div className="flex items-center space-x-2">
                      <img src={question.studentAvatar} alt={question.student} className="w-8 h-8 rounded-full" />
                      <span className="font-medium">{question.student}</span>
                    </div>
                    <span>•</span>
                    <span>{question.timestamp}</span>
                    <span>•</span>
                    <span className="bg-slate-700/20 px-2 py-1 rounded">{question.course}</span>
                  </div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full font-medium capitalize ${
                  question.category === 'exercise' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {question.category}
                </span>
              </div>

              <div className="prose prose-sm max-w-none">
                <p className="text-blue-200">{question.description}</p>
              </div>

              {/* Question Stats */}
              <div className="flex items-center space-x-4 mt-6 pt-4 border-t border-blue-500/30 text-sm text-blue-200">
                <button className="flex items-center space-x-1 hover:text-cyber-600 transition-colors">
                  <ThumbsUp size={16} />
                  <span>{question.helpful} helpful</span>
                </button>
                <span>•</span>
                <span>{question.views} views</span>
              </div>
            </div>
          </div>

          {/* Answers */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white">{question.answers.length} Answers</h2>

            {question.answers.map(answer => (
              <div key={answer.id} className="card">
                <div className="card-body">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3">
                      <img src={answer.avatar} alt={answer.author} className="w-10 h-10 rounded-full" />
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-white">{answer.author}</p>
                          {answer.isInstructor && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded font-medium">
                              Instructor
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-blue-300/70">{answer.timestamp}</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-blue-200 mb-4">{answer.text}</p>

                  <button className="flex items-center space-x-1 text-sm text-blue-200 hover:text-cyber-600 transition-colors">
                    <ThumbsUp size={16} />
                    <span>{answer.helpful} helpful</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Post Answer */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Your Answer</h3>
            </div>
            <div className="card-body space-y-4">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Share your knowledge and help this student..."
                className="input-base"
                rows="4"
              />
              <div className="flex justify-end">
                <button onClick={handlePostReply} className="btn-primary flex items-center space-x-2">
                  <Send size={18} />
                  <span>Post Answer</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Question Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Question Details</h3>
            </div>
            <div className="card-body space-y-3">
              <div>
                <p className="text-sm text-blue-200">Course</p>
                <p className="font-medium text-white">{question.course}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Category</p>
                <p className="font-medium text-white capitalize">{question.category}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Asked by</p>
                <p className="font-medium text-white">{question.student}</p>
              </div>
              <div>
                <p className="text-sm text-blue-200">Answers</p>
                <p className="font-medium text-white">{question.answers.length}</p>
              </div>
            </div>
          </div>

          {/* Related Topics */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Related Topics</h3>
            </div>
            <div className="card-body space-y-2">
              <button className="block text-left w-full p-2 rounded hover:bg-slate-700/30 text-sm text-cyber-600 hover:text-cyber-700 font-medium">
                → Python loops
              </button>
              <button className="block text-left w-full p-2 rounded hover:bg-slate-700/30 text-sm text-cyber-600 hover:text-cyber-700 font-medium">
                → List comprehensions
              </button>
              <button className="block text-left w-full p-2 rounded hover:bg-slate-700/30 text-sm text-cyber-600 hover:text-cyber-700 font-medium">
                → Module 2 exercises
              </button>
            </div>
          </div>

          {/* Get AI Help */}
          <div className="card border-2 border-blue-500/30 bg-slate-800/50">
            <div className="card-body">
              <button className="w-full btn-primary flex items-center justify-center space-x-2">
                <MessageSquare size={20} />
                <span>Ask AI Assistant</span>
              </button>
              <p className="text-xs text-blue-200 text-center mt-2">
                Get instant help from our AI tutor
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
